import { Permission } from './permissions'

export const ROLES = [
  'SYSADMIN',
  'SYSADMIN_TEMPLATE_WRITER',
  'SYSADMIN_TEMPLATE_READER',
  'SYSADMIN_TEMPLATE_ADMIN',

  'ENTITY_OWNER',
  'ENTITY_MANAGER',
  'ENTITY_EMPLOYEE',
] as const

export type Role = (typeof ROLES)[number]

/**
 * Premise roles. An employee holds `EmployeeEntity.roles` (default `ENTITY_TRAINER`); `ENTITY_OWNER` is never stored,
 * it is implied for the premise's owner.
 *
 * Whoever creates reservations and events may delete one that nothing depends on yet (`delete`). Deleting one that is
 * part of the record - started, booked or with participants - is the owner's (`purge`); everyone else cancels
 * (docs/authorization.md, _Deleting_).
 */
const ENTITY_EMPLOYEE_PERMISSIONS = [
  'entity.access:entity',
  'reservation.read:entity',
  'reservation.create:entity',
  'reservation.update:entity',
  'reservation.cancel:entity',
  'reservation.delete:entity',
  'event.delete:entity',
  // Provisional: every premise role adds participants until the employee portal decides (docs/authorization.md).
  'eventParticipation.create:entity',
  'eventParticipation.cancel:entity',
  // Provisional, as above: every premise role sees what participants and bookers wrote until the portal decides.
  'customerNote.read:entity',
] as const satisfies readonly Permission[]

const ENTITY_MANAGER_PERMISSIONS = [
  ...ENTITY_EMPLOYEE_PERMISSIONS,
  'entity.manage:entity',
  'event.cancel:entity',
  'eventParticipation.delete:entity',
  'media.manage:entity',
] as const satisfies readonly Permission[]

const ENTITY_OWNER_PERMISSIONS = [
  ...ENTITY_MANAGER_PERMISSIONS,
  'reservation.purge:entity',
  'event.purge:entity',
  'billing.manage:entity',
] as const satisfies readonly Permission[]

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  SYSADMIN: ['sysadmin.login'],
  SYSADMIN_TEMPLATE_WRITER: [
    'emailtemplate.read:any',
    'emailtemplate.create:any',
    'emailtemplate.update:any',
    'emailtemplate.send:any',
  ],
  SYSADMIN_TEMPLATE_READER: ['emailtemplate.read:any'],
  SYSADMIN_TEMPLATE_ADMIN: [
    'emailtemplate.read:any',
    'emailtemplate.create:any',
    'emailtemplate.update:any',
    'emailtemplate.delete:any',
    'emailtemplate.send:any',
    'emailtemplate.publish:any',
  ],
  ENTITY_OWNER: ENTITY_OWNER_PERMISSIONS,
  ENTITY_MANAGER: ENTITY_MANAGER_PERMISSIONS,
  ENTITY_EMPLOYEE: ENTITY_EMPLOYEE_PERMISSIONS,
} as const
